import type { ApiScope } from "../../../api/public/v1/scopes";
import { FeatureFlag, type FeatureFlagKey } from "../feature-flags/feature-flags.constants";

/**
 * The catalog of first-party skills an org admin can enable on a single agent.
 *
 * **Static on purpose, not a Prisma table.** Adding a first-party skill always
 * ships a new `SKILL.md` bundle through a code deploy anyway (external skills
 * repo -> `gen:skills` -> publish `@agent-nexus/cli` -> re-bake the snapshot),
 * so there is no scenario where an admin toggles a new skill into existence
 * without one. That makes this a value defining a SHAPE the code compiles
 * against, so it belongs in `@nexus/types` next to the `SKILLS` generated const
 * it mirrors, rather than in a table an admin edits. The PER-AGENT enablement row
 * (`AgentFirstPartySkill`) is still a Prisma table — only the list of skills
 * that exist at all is static.
 *
 * `AgentFirstPartySkill.skillSlug` is a `VarChar(128)`, not a DB enum, and is
 * validated against this catalog at write time. A slug that leaves the catalog
 * therefore leaves stale rows behind rather than blocking a migration — see
 * `isFirstPartySkillSlug`, which every read path must use before trusting a
 * stored slug.
 */

/** Every skill slug this platform version knows how to enable. */
export const FIRST_PARTY_SKILL_SLUGS = ["nexus-vibe"] as const;

export type FirstPartySkillSlug = (typeof FIRST_PARTY_SKILL_SLUGS)[number];

export interface FirstPartySkillCatalogEntry {
  readonly slug: FirstPartySkillSlug;
  /** Human label for the agent-builder toggle. */
  readonly title: string;
  /** One sentence: what the agent gains. Rendered under the toggle. */
  readonly description: string;
  /**
   * Whether the agent's model must support the code interpreter. Every skill
   * today drives the baked `@agent-nexus/cli`, which only exists inside a
   * code-interpreter sandbox, so enabling one on a plain chat agent would
   * produce a skill the agent can never successfully run.
   */
  readonly requiresCodeInterpreter: boolean;
  /**
   * Whether the sandbox must be built from the CLI-baked snapshot flavor.
   * `CodeInterpreterSessionService.agentRequiresNexusCli` counts enabled rows
   * today rather than reading this, which is correct only while every entry
   * says `true`; it reads this field the moment one does not.
   */
  readonly requiresNexusCli: boolean;
  /**
   * Org feature flag gating the skill, or `null` when ungated. Typed as
   * `FeatureFlagKey` rather than `string` so the consumer can hand it straight
   * to `CheckFeatureEnabledUseCase` — a `string` here would force a cast at
   * every call site, which is where a typo in a flag key would stop being a
   * compile error and start being an org silently failing the check.
   */
  readonly requiredFeatureFlag: FeatureFlagKey | null;
  /**
   * Scopes the minted key always carries. Read-only by construction: an
   * enablement with no create grant must be genuinely unable to write, rather
   * than relying on the agent not to call a write route.
   */
  readonly baseScopes: readonly ApiScope[];
  /**
   * Scopes added ONLY when the enablement row sets `canCreateGitProjects`.
   * A create grant cannot be pre-scoped to ids that do not exist yet, so it is
   * an org-level permission and an explicit opt-in — never implied by enabling
   * the skill. See `AgentFirstPartySkill.canCreateGitProjects`.
   */
  readonly createGrantScopes: readonly ApiScope[];
  /** Label for the separate create-grant checkbox. */
  readonly createGrantLabel: string;
}

export const FIRST_PARTY_SKILL_CATALOG: Readonly<
  Record<FirstPartySkillSlug, FirstPartySkillCatalogEntry>
> = {
  "nexus-vibe": {
    slug: "nexus-vibe",
    title: "Vibe apps",
    description:
      "Let this agent read your Vibe git projects and apps, and push generated code to the ones you grant it.",
    requiresCodeInterpreter: true,
    requiresNexusCli: true,
    requiredFeatureFlag: FeatureFlag.VIBE,
    baseScopes: ["vibe:read"],
    createGrantScopes: ["vibe:write"],
    createGrantLabel: "This agent may create new Vibe git projects and apps"
  }
};

/** Narrow an arbitrary stored string to a slug this build still knows. */
export function isFirstPartySkillSlug(slug: string): slug is FirstPartySkillSlug {
  return Object.prototype.hasOwnProperty.call(FIRST_PARTY_SKILL_CATALOG, slug);
}

/** The catalog entry for a slug, or `undefined` when the slug is unknown. */
export function getFirstPartySkill(slug: string): FirstPartySkillCatalogEntry | undefined {
  return isFirstPartySkillSlug(slug) ? FIRST_PARTY_SKILL_CATALOG[slug] : undefined;
}

/**
 * The exact scope set a minted key gets for one enablement.
 *
 * Deliberately the ONLY place that decides it, so the mint path and any later
 * reconcile cannot drift: a key whose grant is recomputed here and compared to
 * what is stored is the same check in both directions.
 */
export function firstPartySkillScopes(
  entry: FirstPartySkillCatalogEntry,
  canCreateGitProjects: boolean
): ApiScope[] {
  return canCreateGitProjects
    ? [...entry.baseScopes, ...entry.createGrantScopes]
    : [...entry.baseScopes];
}
